"use client"

import { useState, useEffect } from "react"
import { FiUsers, FiMessageCircle, FiFileText, FiFolder, FiCalendar } from "react-icons/fi"
import { MetricsCard } from "./MetricsCard"
import { StatsChart } from "./StatsChart"
import { VaultTable } from "./VaultTable"

export default function HomeView({ setShowFoldersView }) {
  const [stats, setStats] = useState({
    casosGanados: 0,
    ingresosMensuales: 0,
    clientesNuevos: 0,
    consultasRealizadas: 0,
  })

  // Simulación de números que crecen estadísticamente
  useEffect(() => {
    const targetValues = {
      casosGanados: 189,
      ingresosMensuales: 275000,
      clientesNuevos: 64,
      consultasRealizadas: 412,
    }

    const interval = setInterval(() => {
      setStats((prevStats) => ({
        casosGanados:
          prevStats.casosGanados < targetValues.casosGanados
            ? prevStats.casosGanados + Math.ceil((targetValues.casosGanados - prevStats.casosGanados) / 20)
            : targetValues.casosGanados,
        ingresosMensuales:
          prevStats.ingresosMensuales < targetValues.ingresosMensuales
            ? prevStats.ingresosMensuales +
              Math.ceil((targetValues.ingresosMensuales - prevStats.ingresosMensuales) / 20)
            : targetValues.ingresosMensuales,
        clientesNuevos:
          prevStats.clientesNuevos < targetValues.clientesNuevos
            ? prevStats.clientesNuevos + Math.ceil((targetValues.clientesNuevos - prevStats.clientesNuevos) / 20)
            : targetValues.clientesNuevos,
        consultasRealizadas:
          prevStats.consultasRealizadas < targetValues.consultasRealizadas
            ? prevStats.consultasRealizadas +
              Math.ceil((targetValues.consultasRealizadas - prevStats.consultasRealizadas) / 20)
            : targetValues.consultasRealizadas,
      }))
    }, 100)

    return () => clearInterval(interval)
  }, [])

  // Datos de ejemplo para las gráficas
  const generateChartData = (trend) => {
    const data = []
    let value = 30 + Math.random() * 10

    for (let i = 0; i < 12; i++) {
      if (trend === "up") {
        value += Math.random() * 5
      } else if (trend === "down") {
        value -= Math.random() * 3
      } else {
        value += (Math.random() - 0.5) * 8
      }
      value = Math.max(value, 10)
      data.push(value)
    }
    return data
  }

  const quickAccessItems = [
    {
      title: "Bufetes recomendados",
      value: "50",
      change: { value: "5", percentage: "5%", isPositive: true },
      icon: FiUsers,
      route: "bufetes",
      chartData: generateChartData("up"),
    },
    {
      title: "Reseñas",
      value: "$1,234",
      change: { value: "123", percentage: "10%", isPositive: true },
      icon: FiMessageCircle,
      route: "reviews",
      chartData: generateChartData("up"),
    },
    {
      title: "Ofertas",
      value: "12",
      change: { value: "2", percentage: "2%", isPositive: true },
      icon: FiFileText,
      route: "offers",
      chartData: generateChartData("fluctuate"),
    },
    {
      title: "Abogados recomendados",
      value: "23",
      change: { value: "1", percentage: "1%", isPositive: true },
      icon: FiUsers,
      route: "lawyers",
      chartData: generateChartData("up"),
    },
  ]

  const handleCardClick = (route) => {
    if (route === "lawyers") {
      // Example: route to abogados
    } else if (route === "bufetes") {
      // Example: route to bufetes
    } else if (route === "offers") {
      // Example: route to offers
    } else if (route === "reviews") {
      // Example: route to reviews
    }
  }

  // Función para renderizar mini gráficas para las tarjetas de acceso rápido
  const renderMiniChart = (data) => {
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min

    const points = data
      .map((value, index) => {
        const x = index * (60 / (data.length - 1))
        const y = 20 - ((value - min) / range) * 20
        return `${x},${y}`
      })
      .join(" ")

    return (
      <svg width="60" height="20" className="ml-auto">
        <polyline points={points} fill="none" stroke="#D4AF37" strokeWidth="1.5" />
      </svg>
    )
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold text-navy-900">Inicio</h1>

      {/* Segmento estadístico con MetricsCard */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Estadísticas del Área Mercantil
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricsCard
            title="Casos Ganados"
            value={stats.casosGanados.toString()}
            change={{
              value: "28",
              percentage: "15%",
              isPositive: true,
            }}
          />

          <MetricsCard
            title="Ingresos Mensuales"
            value={`$${stats.ingresosMensuales.toLocaleString()}`}
            change={{
              value: "$22,000",
              percentage: "8%",
              isPositive: true,
            }}
          />

          <MetricsCard
            title="Clientes Nuevos"
            value={stats.clientesNuevos.toString()}
            change={{
              value: "8",
              percentage: "12%",
              isPositive: true,
            }}
          />

          <MetricsCard
            title="Consultas Realizadas"
            value={stats.consultasRealizadas.toString()}
            change={{
              value: "91",
              percentage: "22%",
              isPositive: true,
            }}
          />
        </div>
      </div>

      {/* Gráfica de estadísticas */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Rendimiento Anual
        </h2>
        <StatsChart />
      </div>

      {/* Tarjetas de acceso rápido con MetricsCard */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Métricas Destacadas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickAccessItems.map((card, index) => (
            <div key={index} onClick={() => handleCardClick(card.route)} className="cursor-pointer">
              <MetricsCard
                title={card.title}
                value={card.value}
                change={card.change}
                chart={renderMiniChart(card.chartData)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Tabla de vaults */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Vaults Activos
        </h2>
        <VaultTable />
      </div>

      {/* Acceso Rápido */}
      {/* <div>
        <h2 className="text-xl font-semibold mb-4" style={{ color: "#1A365D" }}>
          Acceso Rápido
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            className="bg-white p-4 rounded-lg shadow-md transition-all duration-300 hover:shadow-xl flex items-center space-x-3"
            onClick={() => setShowFoldersView(true)}
          >
            <FiFolder className="h-8 w-8" style={{ color: "#D4AF37" }} />
            <div className="text-left">
              <h3 className="font-medium" style={{ color: "#1A365D" }}>
                Carpetas
              </h3>
              <p className="text-sm text-gray-500">Accede a tus documentos</p>
            </div>
          </button>

          <button className="bg-white p-4 rounded-lg shadow-md transition-all duration-300 hover:shadow-xl flex items-center space-x-3">
            <FiCalendar className="h-8 w-8" style={{ color: "#D4AF37" }} />
            <div className="text-left">
              <h3 className="font-medium" style={{ color: "#1A365D" }}>
                Calendario
              </h3>
              <p className="text-sm text-gray-500">Administra tus citas</p>
            </div>
          </button>

          <button className="bg-white p-4 rounded-lg shadow-md transition-all duration-300 hover:shadow-xl flex items-center space-x-3">
            <FiFileText className="h-8 w-8" style={{ color: "#D4AF37" }} />
            <div className="text-left">
              <h3 className="font-medium" style={{ color: "#1A365D" }}>
                Casos
              </h3>
              <p className="text-sm text-gray-500">Visualiza tus casos activos</p>
            </div>
          </button>
        </div>
      </div> */}
    </div>
  )
}

